import PropTypes from 'prop-types';
import { useEffect, useId, useRef } from 'react';
import {
  ACCOUNT_TYPES,
  CHILEAN_BANKS,
  formatRutInput,
  validateRutInput,
} from '@/lib';
import { Input, Select } from '@/ui';

export function validateQuoteBank(form) {
  const errors = {};
  if (!form.bankCode) errors.bankCode = 'Selecciona un banco.';
  if (!form.accountType) errors.accountType = 'Selecciona el tipo de cuenta.';
  if (!form.holderName.trim()) errors.holderName = 'Ingresa el titular.';
  const rutError = validateRutInput(form.rut.trim());
  if (rutError) errors.rut = rutError;
  const number = form.accountNumber.replace(/\D/g, '');
  if (!number) errors.accountNumber = 'Ingresa el número de cuenta.';
  else if (!/^\d{4,20}$/.test(number))
    errors.accountNumber = 'Debe tener entre 4 y 20 dígitos.';
  return {
    errors,
    body: {
      bankCode: form.bankCode,
      accountType: form.accountType,
      holderName: form.holderName.trim(),
      accountNumber: number,
      rut: formatRutInput(form.rut.trim()),
    },
  };
}

export default function QuoteBankFields({
  value,
  errors,
  error,
  errorId,
  disabled,
  onChange,
}) {
  const id = useId();
  const fieldsRef = useRef(null);
  useEffect(() => {
    fieldsRef.current?.querySelector('[aria-invalid="true"]')?.focus();
  }, [errors]);
  const change = (key) => (event) =>
    onChange({
      ...value,
      [key]:
        key === 'accountNumber'
          ? event.target.value.replace(/\D/g, '')
          : event.target.value,
    });
  return (
    <fieldset className="sold-bank" disabled={disabled} ref={fieldsRef}>
      <legend>Cuenta para recibir el pago</legend>
      <p>Quedará guardada en Perfil para tus ofertas.</p>
      {[
        [
          'bankCode',
          'Banco',
          CHILEAN_BANKS.map((bank) => [bank.code, bank.name]),
        ],
        [
          'accountType',
          'Tipo de cuenta',
          ACCOUNT_TYPES.map((type) => [type.value, type.label]),
        ],
        ['accountNumber', 'Número de cuenta'],
        ['holderName', 'Titular'],
        ['rut', 'RUT del titular'],
      ].map(([key, label, options]) => (
        <div key={key} className="sold-control">
          <label htmlFor={`${id}-${key}-input`}>
            <span>{label}</span>
          </label>
          {options ? (
            <Select
              id={`${id}-${key}-input`}
              value={value[key]}
              onChange={change(key)}
              aria-invalid={!!errors[key]}
              error={!!errors[key]}
              aria-describedby={errors[key] ? `${id}-${key}` : undefined}
              placeholder={`Selecciona ${label.toLowerCase()}…`}
            >
              {options.map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </Select>
          ) : (
            <Input
              id={`${id}-${key}-input`}
              value={value[key]}
              onChange={change(key)}
              aria-invalid={!!errors[key]}
              error={!!errors[key]}
              aria-describedby={errors[key] ? `${id}-${key}` : undefined}
              inputMode={key === 'accountNumber' ? 'numeric' : undefined}
              onBlur={
                key === 'rut'
                  ? () => onChange({ ...value, rut: formatRutInput(value.rut) })
                  : undefined
              }
            />
          )}
          {errors[key] ? (
            <small className="sold-form-error" role="alert" id={`${id}-${key}`}>
              {errors[key]}
            </small>
          ) : null}
        </div>
      ))}
      {error ? (
        <p className="sold-form-error" role="alert" id={errorId}>
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}

QuoteBankFields.propTypes = {
  value: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  error: PropTypes.string,
  errorId: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};
